import React from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../hooks/useRTL';
import { formatCurrency } from '../../../utils/formatters';

import { BAR_COLORS, PIE_COLORS, MONTH_NAMES } from '../../../utils/constants';
import TopDoctors from './topDoctors';

const ChartsSection = ({ analytics, analyticsLoading, error }) => {
    const { t } = useTranslation();
    const { containerProps } = useRTL();

    if (analyticsLoading) {
        return (
            <Grid container spacing={3} sx={{ mb: 5, ...containerProps }}>
                <Grid item xs={12}>
                    {error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                            <CircularProgress />
                        </Box>
                    )}
                </Grid>
            </Grid>
        );
    }

    // Prepare chart data
    const monthlyIncomeData = analytics.monthlyIncomeTrend?.map((item, idx) => ({
        month: MONTH_NAMES[(item._id.month - 1) % 12],
        income: item.totalIncome,
        appointments: item.appointmentCount,
        color: BAR_COLORS[idx % BAR_COLORS.length],
    })) || [];

    const scanData = analytics.mostRequestedScans?.map(scan => ({
        name: scan.name,
        count: scan.count,
        revenue: scan.totalRevenue,
    })) || [];

    const appointmentStatusData = [
        {
            name: t('appointments.statusScheduled'),
            value: analytics.recentAppointments?.filter(apt => apt.status === 'scheduled').length || 0
        },
        {
            name: t('appointments.statusCompleted'),
            value: analytics.recentAppointments?.filter(apt => apt.status === 'completed').length || 0
        },
        {
            name: t('appointments.statusCancelled'),
            value: analytics.recentAppointments?.filter(apt => apt.status === 'cancelled').length || 0
        },
    ];

    const renderPieLabel = ({ name, percent, value }) => {
        if (!value) return null;
        return `${name} ${(percent * 100).toFixed(0)}%`;
    };

    return (
        <>
            {/* Charts Row 1 */}
            <Grid container spacing={3} sx={{ mb: 5, ...containerProps }}>
                {/* Monthly Income Trend */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, ...containerProps }}>
                        <Typography variant="h6" gutterBottom>
                            {t('dashboard.monthlyIncomeTrend')}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={monthlyIncomeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" angle={-30} textAnchor="end" interval={0} height={60} />
                                <YAxis />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Bar dataKey="income" name={t('dashboard.totalIncome')} isAnimationActive={false}>
                                    {monthlyIncomeData.map((entry, idx) => (
                                        <Cell key={`cell-bar-${idx}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Most Requested Scans */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, ...containerProps }}>
                        <Typography variant="h6" gutterBottom>
                            {t('dashboard.mostRequestedScans')}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={scanData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={80} />
                                <Tooltip formatter={(value) => [value, t('common.count')]} />
                                <Bar dataKey="count" isAnimationActive={false}>
                                    {scanData.map((entry, idx) => (
                                        <Cell key={`cell-scan-${idx}`} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Charts Row 2 */}
            <Grid container spacing={3} sx={{ mb: 5, ...containerProps }}>
                {/* Appointment Status Distribution */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, ...containerProps }}>
                        <Typography variant="h6" gutterBottom>
                            {t('dashboard.appointmentStatus')}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={appointmentStatusData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={renderPieLabel}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {appointmentStatusData.map((entry, idx) => (
                                        <Cell key={`cell-pie-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Top Rated Doctors */}
                <TopDoctors
                    analytics={analytics}
                    analyticsLoading={analyticsLoading}
                    error={error}
                />
            </Grid>
        </>
    );
};

export default ChartsSection;